import { ThreadTitleTransport } from './thread-title-stream';
import type { UIMessage } from 'ai';
import { loadServerThreadBranch } from '../server-threads';

type Options = ConstructorParameters<typeof ThreadTitleTransport>[0] & {
  attachmentReferences?: boolean;
};

export class SubmittedTurnChatTransport extends ThreadTitleTransport {
  private readonly remember: (messages: UIMessage[]) => Promise<void>;

  constructor(
    { attachmentReferences = false, ...options }: Options,
    remember: (messages: UIMessage[]) => Promise<void>,
  ) {
    super({
      ...options,
      ...(attachmentReferences
        ? {
            prepareSendMessagesRequest: async (request) => {
              const previous = await options.prepareSendMessagesRequest?.(request);
              const message = request.messages.at(-1);
              if (message?.role !== 'user')
                throw new Error(
                  'This turn is unavailable. Reload the chat and send a new message.',
                );
              const body = {
                ...request.body,
                ...(previous?.body ?? {}),
                id: request.id,
                trigger: request.trigger,
                messageId: request.messageId,
                submittedMessageId: message.id,
                expectedHeadId:
                  loadServerThreadBranch(String(request.body?.thread_id ?? request.id))?.headId ??
                  null,
              };
              // The native SDK still initializes its real runtime/answer and
              // resume state. Only its HTTP serialization becomes an ID lookup.
              delete (body as Record<string, unknown>).messages;
              return { ...previous, body };
            },
          }
        : {}),
    });
    this.remember = remember;
  }

  override async sendMessages(
    options: Parameters<ThreadTitleTransport['sendMessages']>[0],
    afterRemember?: (
      options: Parameters<ThreadTitleTransport['sendMessages']>[0],
    ) => Promise<Parameters<ThreadTitleTransport['sendMessages']>[0]>,
  ) {
    options.abortSignal?.throwIfAborted();
    await this.remember(options.messages);
    options.abortSignal?.throwIfAborted();
    const submitted = afterRemember ? await afterRemember(options) : options;
    options.abortSignal?.throwIfAborted();
    // Keep SDK thread initialization and request-body construction intact.
    return super.sendMessages(submitted);
  }
}
